import { QueryErrorResetBoundary } from "@tanstack/react-query";
import { AlertTriangle } from "lucide-react";
import { type ReactNode, useEffect, useRef } from "react";
import { ErrorBoundary, type FallbackProps } from "react-error-boundary";

/**
 * RouteShell — combines TanStack Query's `QueryErrorResetBoundary` with
 * `react-error-boundary` to give every wrapped route the explicit "error" branch
 * of the 4-state model (loading / empty / error / loaded).
 *
 * The canonical wiring: queries inside RouteShell opt into `throwOnError: true`
 * (per query, so existing inline `if (error)` branches are not disturbed for
 * queries the route prefers to handle manually). When a wrapped query throws,
 * React's error boundary catches it and this shell renders the fallback card.
 * "Try again" calls both the reset passed by QueryErrorResetBoundary AND the
 * boundary's own resetErrorBoundary — TanStack's reference pattern.
 *
 * Security posture: `TRPCError.message` is user-facing per .claude/rules/security.md,
 * so we display it as-is (backend already sanitises). We do NOT show stack traces,
 * URLs, or component stacks.
 */

type RouteShellProps = {
	children: ReactNode;
	/** Optional render-prop to override the default fallback card. */
	fallback?: (error: Error, reset: () => void) => ReactNode;
};

export function RouteShell({ children, fallback }: RouteShellProps) {
	return (
		<QueryErrorResetBoundary>
			{({ reset }) => (
				<ErrorBoundary
					onReset={reset}
					fallbackRender={({ error, resetErrorBoundary }: FallbackProps) => {
						const err = error instanceof Error ? error : new Error(String(error));
						if (fallback) return <>{fallback(err, resetErrorBoundary)}</>;
						return <DefaultErrorCard error={err} onRetry={resetErrorBoundary} />;
					}}
				>
					{children}
				</ErrorBoundary>
			)}
		</QueryErrorResetBoundary>
	);
}

type DefaultErrorCardProps = {
	error: Error;
	onRetry: () => void;
};

function DefaultErrorCard({ error, onRetry }: DefaultErrorCardProps) {
	const retryRef = useRef<HTMLButtonElement>(null);
	// Focus the retry button when the fallback mounts so keyboard + screen-reader
	// users land on the actionable element, not the (already-spoken) alert region.
	useEffect(() => {
		retryRef.current?.focus();
	}, []);

	// R18 (2026-04-19): flipped raw `bg-[#hex]`/`text-[#hex]` on the brand
	// palette (destructive red + primary indigo) to the `brand-*` semantic
	// tokens defined in `styles.css`. Neutral text hexes retained for now —
	// they have existing semantic tokens (`--color-foreground`,
	// `--color-muted-foreground`) and their migration is a separate pass.
	return (
		<div
			role="alert"
			aria-live="assertive"
			className="flex flex-col items-center rounded-lg border border-brand-destructive/20 bg-brand-destructive-soft py-10"
		>
			<AlertTriangle className="h-8 w-8 text-brand-destructive" strokeWidth={1.5} />
			<h2 className="mt-3 text-sm font-semibold text-[#1e1e20]">Something went wrong</h2>
			<p className="mt-1 max-w-md px-4 text-center text-xs text-[#6b7280]">
				{error.message || "An unexpected error occurred. Please try again."}
			</p>
			<button
				ref={retryRef}
				type="button"
				onClick={onRetry}
				className="mt-4 rounded bg-brand-primary px-4 py-1.5 text-sm font-semibold text-white transition-colors hover:bg-brand-primary-hover focus:outline-none focus:ring-2 focus:ring-brand-primary/40"
			>
				Try again
			</button>
		</div>
	);
}
